import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import { PLATFORMS } from '../../lib/growthAds';
import { MIN_SAMPLE, addDays, dayStart, fmtAt, rangeLabel, rangeWindow, readRange, riyadhDay, spanLabel, writeRange } from '../../lib/growthRange';
import {
  CARD_AR, breakEven, campaignProfit, fetchCampaignInputs, fetchProfitData, fetchProfitSettings, readSettings,
  removeProfitSetting, saveProfitSetting, summarizeProfit,
} from '../../lib/growthProfit';

// Growth OS «الربحية» — owner, 2026-11-01. Net profit per paid order with every
// component labelled «فعلي» / «تقديري» and its source, the 299 request fees,
// campaign profit and the orders needed to break even. The assumptions are
// edited at the bottom (growth_profit_settings, owner-only).

const LOW = 'بيانات قليلة';
const fmt = (n, d = 2) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: d }));
const sar = (n) => (n == null ? '—' : <span dir="rtl">{fmt(n)} ر.س</span>);

function Tag({ actual, source }) {
  return <span className={`gos-ptag ${actual ? 'actual' : 'est'}`} title={source}>{actual ? 'فعلي' : 'تقديري'}</span>;
}

function Hero({ label, value, sub, low, base }) {
  return (
    <div className="gos-kpi" data-hero={label}>
      <p className="gos-kpi-label">{label}</p>
      <p className={`gos-kpi-value${low ? ' muted' : ''}`} style={low ? { direction: 'rtl' } : undefined}>{value}</p>
      <div className="gos-kpi-foot">{sub}{low && <span className="gos-pill low" data-low-sample="1" title={`أقل من ${MIN_SAMPLE} طلباً: لا يُبنى عليه قرار`}>{LOW} ({fmt(base, 0)})</span>}</div>
    </div>
  );
}

function OrdersTable({ orders }) {
  if (!orders.length) return <div className="gos-empty-state"><strong>لا توجد طلبات مدفوعة في هذه الفترة</strong>تظهر هنا كل طلبات المتجر المدفوعة فعلاً (طلبات الفريق وطلب الاختبار مستبعدة).</div>;
  const cols = [['product', 'البضاعة'], ['domestic', 'شحن داخل الصين'], ['hiobuy_fee', 'رسوم HioBuy'], ['services', 'خدمات'], ['shipping', 'الشحن الدولي'], ['import', 'الجمارك'], ['payment', 'رسوم الدفع'], ['refund', 'الاسترداد']];
  return (
    <div className="gos-table-scroll">
      <table className="gos-table gos-table-wide" data-orders="1">
        <thead><tr><th>الطلب</th><th>وقت الدفع</th><th>البطاقة</th><th>المدفوع</th>{cols.map(([, a]) => <th key={a}>{a}</th>)}<th>صافي الربح</th></tr></thead>
        <tbody>{orders.map((o) => {
          const c = Object.fromEntries(o.components.map((x) => [x.key, x]));
          return (
            <tr key={o.id} data-order={o.order_no}>
              <td dir="ltr" style={{ textAlign: 'right' }}>{o.order_no}</td>
              <td className="gos-when">{fmtAt(o.paid_at)}</td>
              <td>{CARD_AR[o.card_class] || '—'}</td>
              <td className="num">{sar(o.revenue)} <Tag actual source="المبلغ المدفوع فعلاً عبر Telr" /></td>
              {cols.map(([k]) => (
                <td key={k} className="num">{c[k] ? <>{sar(c[k].sar)} <Tag actual={c[k].actual} source={c[k].source} /></> : <span className="gos-faint">—</span>}</td>
              ))}
              <td className={`num gos-net ${o.net >= 0 ? 'pos' : 'neg'}`}><b>{sar(o.net)}</b></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function FeesTable({ fees }) {
  if (!fees.length) return <p className="gos-faint" style={{ margin: 0 }}>لا رسوم طلب مدفوعة في هذه الفترة.</p>;
  return (
    <div className="gos-table-scroll">
      <table className="gos-table" data-fees="1">
        <thead><tr><th>وقت الدفع</th><th>الرسوم</th><th>رسوم الدفع (Telr)</th><th>الصافي</th></tr></thead>
        <tbody>{fees.map((f) => (
          <tr key={f.id}>
            <td className="gos-when">{fmtAt(f.paid_at)}</td>
            <td className="num">{sar(f.revenue)} <Tag actual source="المبلغ المدفوع" /></td>
            <td className="num">{sar(f.telr.sar)} <Tag actual={f.telr.actual} source={f.telr.source} /></td>
            <td className="num"><b>{sar(f.net)}</b></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Campaigns({ orders, range }) {
  const [inputs, setInputs] = useState(undefined);
  const [touch, setTouch] = useState('last_touch');
  const ids = orders.map((o) => o.id).join(',');
  useEffect(() => {
    let alive = true; setInputs(undefined);
    fetchCampaignInputs(orders.map((o) => o.id), range).then((r) => { if (alive) setInputs(r); }).catch(() => { if (alive) setInputs(null); });
    return () => { alive = false; };
  }, [ids, range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = useMemo(() => (inputs ? campaignProfit({ orders, ...inputs }, touch) : []), [inputs, orders, touch]);
  const platformAr = (k) => (PLATFORMS.find((p) => p.key === k) || {}).ar || '';
  return (
    <section className="gos-card" data-section="campaigns" style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>ربح الحملات</h3></div>
      <div className="gos-range" role="group" aria-label="النسبة" style={{ marginBottom: 12 }}>
        <button type="button" className={touch === 'last_touch' ? 'on' : ''} onClick={() => setTouch('last_touch')}>آخر مصدر قبل الطلب</button>
        <button type="button" className={touch === 'first_touch' ? 'on' : ''} onClick={() => setTouch('first_touch')}>أول مصدر (30 يوماً)</button>
      </div>
      {inputs === undefined ? <div className="gos-empty-state">…</div> : inputs === null ? <div className="gos-empty-state">تعذّر التحميل.</div>
        : !list.length ? <div className="gos-empty-state"><strong>لا إنفاق ولا طلبات منسوبة في هذه الفترة</strong></div> : (
          <div className="gos-table-scroll">
            <table className="gos-table gos-table-wide" data-campaigns="1">
              <thead><tr><th>الحملة</th><th>الإنفاق</th><th>طلبات مدفوعة</th><th>صافي ربح الطلبات</th><th>الربح بعد الإعلان</th><th>تكلفة الطلب</th></tr></thead>
              <tbody>{list.map((r) => {
                const low = r.orders < MIN_SAMPLE;
                return (
                  <tr key={`${r.platform}|${r.campaign_id}`}>
                    <td><div>{r.name || r.campaign_id || '—'}</div>{r.platform && <small className="gos-faint">{platformAr(r.platform)}{r.campaign_id ? <> · <bdi dir="ltr">{r.campaign_id}</bdi></> : null}</small>}</td>
                    <td className="num">{sar(r.spend)}</td>
                    <td className="num">{fmt(r.orders, 0)}</td>
                    <td className="num">{sar(r.net)}</td>
                    <td className={`num gos-net ${r.afterAds >= 0 ? 'pos' : 'neg'}`}><span className={low ? 'gos-rate low' : ''} data-low-sample={low ? '1' : '0'}>{sar(r.afterAds)}</span></td>
                    <td className="num"><span className={low ? 'gos-rate low' : 'gos-rate'} title={low ? `${LOW} (${r.orders}؛ الحد ${MIN_SAMPLE})` : undefined}>{r.perOrder == null ? '—' : sar(r.perOrder)}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      <p className="gos-note">الطلب يُنسب للحملة بقاعدة الروابط نفسها في «إدارة الحملات». أرقام أقل من {MIN_SAMPLE} طلباً باهتة: {LOW}.</p>
    </section>
  );
}

// ── settings ─────────────────────────────────────────────────────────────────
const FIELDS = {
  telr_fee: [['pct', '%'], ['fixed_sar', 'ر.س ثابتة']],
  vat: [['value', '%']],
  fixed_monthly: [['amount_sar', 'ر.س شهرياً']],
  fx: [['value', 'ر.س لكل يوان']],
  hiobuy: [['pct', '% من البضاعة']],
  shipping: [['rate_air_kg', 'ر.س/كجم جوي'], ['rate_sea_cbm', 'ر.س/م³ بحري']],
  exclude_order: [],
};
const GROUPS = [
  ['telr_fee', 'رسوم Telr (تُضاف الضريبة عليها)'], ['vat', 'الضريبة'], ['fixed_monthly', 'التكاليف الثابتة الشهرية'],
  ['fx', 'سعر الصرف'], ['hiobuy', 'رسوم HioBuy (حين لا يُسجّل إجمالي الطلب)'], ['shipping', 'أسعار الشحن لكل وكيل'], ['exclude_order', 'طلبات مستبعدة'],
];

function SettingRow({ row, onSaved }) {
  const [r, setR] = useState(row);
  const [state, setState] = useState(null);
  const dirty = JSON.stringify(r) !== JSON.stringify(row);
  const set = (k, v) => setR({ ...r, [k]: v });
  const save = async () => {
    setState('…');
    try { await saveProfitSetting({ ...r, ...Object.fromEntries((FIELDS[r.kind] || []).map(([k]) => [k, r[k] === '' || r[k] == null ? null : Number(r[k])])) }); setState('حُفظ'); onSaved(); } catch (e) { setState(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  const del = async () => { if (!window.confirm(`حذف «${r.label_ar}»؟`)) return; try { await removeProfitSetting(r.key); onSaved(); } catch (e) { setState(`تعذّر الحذف: ${e?.message || e}`); } };
  const removable = ['fixed_monthly', 'shipping', 'exclude_order'].includes(r.kind);
  return (
    <div className="gos-srow" data-setting={r.key}>
      <input className="gos-sinput name" value={r.label_ar} onChange={(e) => set('label_ar', e.target.value)} aria-label="الاسم" dir={r.kind === 'exclude_order' ? 'ltr' : 'auto'} />
      {(FIELDS[r.kind] || []).map(([k, unit]) => (
        <label key={k} className="gos-sfield"><input className="gos-sinput" type="number" step="any" value={r[k] ?? ''} onChange={(e) => set(k, e.target.value)} dir="ltr" /><span>{unit}</span></label>
      ))}
      {r.kind === 'fixed_monthly' && <label className="gos-scheck"><input type="checkbox" checked={!!r.vat_applies} onChange={(e) => set('vat_applies', e.target.checked)} /> + ضريبة</label>}
      {r.kind === 'shipping' && <label className="gos-scheck"><input type="checkbox" checked={!!r.is_default} onChange={(e) => set('is_default', e.target.checked)} /> المستخدم</label>}
      {r.kind !== 'exclude_order' && <label className="gos-scheck"><input type="checkbox" checked={!!r.is_estimate} onChange={(e) => set('is_estimate', e.target.checked)} /> تقديري</label>}
      <input className="gos-sinput note" value={r.note || ''} onChange={(e) => set('note', e.target.value)} placeholder="المصدر / ملاحظة" aria-label="ملاحظة" />
      <button type="button" className="gos-btn" disabled={!dirty} onClick={save} style={{ flex: '0 0 auto' }}>حفظ</button>
      {removable && <button type="button" className="gos-btn ghost" onClick={del} style={{ flex: '0 0 auto' }}>حذف</button>}
      {state && <span className="gos-faint">{state}</span>}
    </div>
  );
}

function Settings({ rows, reload }) {
  const add = async (kind) => {
    const key = `${kind}_${Date.now().toString(36)}`;
    const base = { key, kind, label_ar: kind === 'exclude_order' ? 'MK-' : 'جديد', is_estimate: kind !== 'exclude_order', sort: 100 };
    try { await saveProfitSetting(base); reload(); } catch (e) { window.alert(`تعذّر الإضافة: ${e?.message || e}`); }
  };
  return (
    <details className="gos-card gos-tech" data-section="settings" open={false}>
      <summary>الإعدادات والافتراضات</summary>
      <p className="gos-card-sub" style={{ marginTop: 10 }}>كل رقم يستخدمه هذا القسم. «تقديري» يعني أنه افتراض لا فاتورة؛ اكتب مصدره في الملاحظة.</p>
      {GROUPS.map(([kind, title]) => (
        <div key={kind} className="gos-sgroup" data-kind={kind}>
          <h4 className="gos-subhead">{title}</h4>
          {rows.filter((r) => r.kind === kind).map((r) => <SettingRow key={`${r.key}:${r.updated_at}`} row={r} onSaved={reload} />)}
          {['fixed_monthly', 'shipping', 'exclude_order'].includes(kind) && (
            <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto', marginTop: 6 }} onClick={() => add(kind)}>+ إضافة</button>
          )}
        </div>
      ))}
    </details>
  );
}

export default function GrowthProfit({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const [rows, setRows] = useState(undefined);
  const [data, setData] = useState(undefined);
  const [month, setMonth] = useState(undefined);
  const loadSettings = () => fetchProfitSettings().then(setRows).catch((e) => { console.warn('[GrowthProfit] settings:', e?.message || e); setRows(null); });
  useEffect(() => { loadSettings(); }, []);
  useEffect(() => {
    let alive = true; setData(undefined);
    fetchProfitData(win.start, win.end).then((d) => { if (alive) setData(d); }).catch((e) => { console.warn('[GrowthProfit] data:', e?.message || e); if (alive) setData(null); });
    return () => { alive = false; };
  }, [win.start, win.end]);
  useEffect(() => {
    const today = riyadhDay(); const first = `${today.slice(0, 7)}-01`;
    fetchProfitData(dayStart(first), dayStart(addDays(today, 1))).then(setMonth).catch(() => setMonth(null));
  }, []);
  const s = useMemo(() => (rows ? readSettings(rows) : null), [rows]);
  const p = useMemo(() => (s && data ? summarizeProfit(data, s, win) : null), [s, data, win]);
  const be = p && s ? breakEven(s.monthlyFixed, p.avg) : null;
  const monthOrders = month ? (month.orders || []).length : null;
  const low = p ? p.orders.length < MIN_SAMPLE : false;

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="profitability" title="الربحية"
                 actions={<span className="gos-top-range">{spanLabel(range.from, range.to)}</span>}>
      <div className="gos-hello">
        <h2>الربحية</h2>
        <p>{rangeLabel(range)}. صافي الربح من كل طلب مدفوع بعد تكلفة المنتج والشحن والجمارك ورسوم الدفع. كل رقم معلَّم «فعلي» أو «تقديري» مع مصدره.</p>
      </div>
      <RangeBar range={range} onChange={(next) => setParams(writeRange(next))} compare={false} />
      {(rows === null || data === null) && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل البيانات</strong>أعد تحميل الصفحة.</div></div>}
      {!p ? (rows !== null && data !== null && <div className="gos-card"><div className="gos-empty-state">…</div></div>) : (
        <>
          <div className="gos-kpis gos-kpis-4" style={{ marginBottom: 12 }}>
            <Hero label="صافي الربح للفترة" value={sar(p.net)} low={low} base={p.orders.length}
                  sub={<span>من {fmt(p.orders.length, 0)} طلب + رسوم الطلب · بعد التكاليف الثابتة: {sar(p.afterFixed)}
                    <span className="gos-ship-warn" data-ship-warning="1">سعر الشحن غير محسوم بعد، الربح الفعلي ممكن يكون أقل</span></span>} />
            <Hero label="متوسط ربح الطلب" value={sar(p.avg)} low={low} base={p.orders.length} sub={<span>قبل التكاليف الثابتة</span>} />
            <Hero label="متوسط قيمة الطلب" value={sar(p.aov)} low={low} base={p.orders.length} sub={<span>ما دفعه العميل في الطلب المدفوع</span>} />
            <Hero label="طلبات للتعادل شهرياً" value={be?.needed == null ? '—' : fmt(be.needed, 0)} low={low} base={p.orders.length}
                  sub={be?.needed == null ? <span>{be?.why}</span> : <span>هذا الشهر: {fmt(monthOrders, 0)} من {fmt(be.needed, 0)}</span>} />
          </div>
          {be?.needed != null && monthOrders != null && (
            <div className="gos-card gos-breakeven" data-section="breakeven" style={{ marginBottom: 16 }}>
              <div className="gos-be-bar"><span style={{ width: `${Math.min(100, (monthOrders / be.needed) * 100)}%` }} /></div>
              <p className="gos-card-sub" style={{ margin: '8px 0 0' }}>
                التكاليف الثابتة {sar(s.monthlyFixed)} شهرياً ÷ متوسط ربح الطلب {sar(p.avg)} = {fmt(be.needed, 0)} طلباً في الشهر. وصلنا هذا الشهر إلى {fmt(monthOrders, 0)}.
              </p>
            </div>
          )}

          <div className="gos-plists" style={{ marginBottom: 16 }}>
            <section className="gos-card" data-section="revenue">
              <div className="gos-card-head"><h3>الإيراد</h3></div>
              <table className="gos-table"><tbody>
                <tr><td>المدفوع في طلبات المتجر</td><td className="num">{sar(p.revenue)} <Tag actual source="Telr" /></td></tr>
                <tr><td>رسوم الطلب (299)</td><td className="num">{sar(p.feeRevenue)} <Tag actual source="managed_request_fees" /></td></tr>
                <tr><td><b>صافي رسوم الطلب بعد Telr</b></td><td className="num"><b>{sar(p.feesNet)}</b></td></tr>
              </tbody></table>
            </section>
            <section className="gos-card" data-section="breakdown">
              <div className="gos-card-head"><h3>تكاليف الطلبات</h3></div>
              <table className="gos-table"><tbody>
                {p.breakdown.map((b) => (
                  <tr key={b.key}><td>{b.ar}</td><td className="num">{sar(b.sar)}</td>
                    <td className="gos-faint" style={{ fontSize: 12 }}>{b.actual ? `${fmt(b.actual, 0)} فعلي` : ''}{b.actual && b.estimate ? ' · ' : ''}{b.estimate ? `${fmt(b.estimate, 0)} تقديري` : ''}</td></tr>
                ))}
                <tr><td>التكاليف الثابتة لهذه الفترة</td><td className="num">{sar(p.fixedShare)}</td><td className="gos-faint" style={{ fontSize: 12 }}>{fmt(p.days, 0)} يوماً من {fmt(s.monthlyFixed)} شهرياً</td></tr>
              </tbody></table>
            </section>
          </div>

          <section className="gos-card" data-section="orders" style={{ marginBottom: 16 }}>
            <div className="gos-card-head"><h3>كل طلب مدفوع</h3><span className="gos-pcount">{fmt(p.orders.length, 0)} طلب</span></div>
            <p className="gos-card-sub">مرّر المؤشر على «فعلي» أو «تقديري» لترى المصدر. طلبات الفريق وطلب الاختبار MK-261021-00003 مستبعدة.</p>
            <OrdersTable orders={p.orders} />
          </section>

          <section className="gos-card" data-section="fees" style={{ marginBottom: 16 }}>
            <div className="gos-card-head"><h3>رسوم الطلب (299 ر.س)</h3><span className="gos-pcount">{fmt(p.fees.length, 0)}</span></div>
            <FeesTable fees={p.fees} />
          </section>

          <Campaigns orders={p.orders} range={range} />
        </>
      )}
      {rows && <Settings rows={rows} reload={loadSettings} />}
    </GrowthShell>
  );
}
